/**
 * Searchable Select Component
 * Dropdown with search functionality
 *
 * The menu is rendered via createPortal so it is not clipped by overflow:auto
 * ancestors (e.g. filter drawers with z-index stacking).
 */

import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, X, Search } from 'lucide-react'
import { cn } from '@lib/utils/helpers'

/** Above typical app overlays / drawers (see filter panels using z-[2147483647]). */
const MENU_PORTAL_Z = 2147483647

export interface SearchableSelectOption {
  value: string
  label: string
  [key: string]: unknown
}

interface SearchableSelectProps {
  options: SearchableSelectOption[]
  value?: string | string[]
  onChange: (value: string | string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  error?: string
  showErrorMessage?: boolean
  disabled?: boolean
  multiple?: boolean
  searchable?: boolean
  onSearch?: (searchText: string) => void
  /** Debounce delay in ms before calling onSearch (e.g. for API-backed dropdowns). Default 400. */
  searchDebounceMs?: number
  /**
   * When true, paste into the search field calls onSearch immediately with the post-paste value
   * (Angular reports-invites: paste → loadCompany/loadProperty without debounce).
   */
  immediateSearchOnPaste?: boolean
  /**
   * Optional paste-specific handler. When provided (with `immediateSearchOnPaste`), paste calls this
   * instead of `onSearch` — lets a consumer bypass a throttled `onSearch` on paste (Angular
   * `pasteData` → `loadCompany`/`loadProperty` directly, no even-length throttle).
   */
  onPasteSearch?: (searchText: string) => void
  onOpen?: () => void
  /**
   * When false, closing the menu does not call `onSearch('')`.
   * Use for API-backed selects that already refresh on `onOpen` so closing does not re-fetch.
   */
  invokeSearchOnClose?: boolean
  /** Forward to react-hook-form `field.onBlur` so touched state and validation stay in sync. */
  onBlur?: () => void
  loading?: boolean
  className?: string
  /** Layout for selected-value chips when `multiple` is true. */
  selectedChipsLayout?: 'column' | 'grid-2' | 'flex-wrap'
  /** Optional stable id for E2E tests (applied to the trigger button). */
  triggerDataTestId?: string
  /**
   * Where the menu opens relative to the trigger.
   * `auto` flips above when there is not enough space below (default).
   * `bottom` / `top` force placement.
   */
  menuPlacement?: 'auto' | 'bottom' | 'top'
}

const DEFAULT_SEARCH_DEBOUNCE_MS = 400

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  error,
  showErrorMessage = true,
  disabled = false,
  multiple = false,
  searchable = true,
  onSearch,
  searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
  onPasteSearch,
  onOpen,
  invokeSearchOnClose = true,
  onBlur,
  loading = false,
  className,
  selectedChipsLayout = 'column',
  triggerDataTestId,
  menuPlacement = 'auto',
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Always the latest typed query — debounced timer reads this so we never call `onSearch` with a stale prefix. */
  const latestSearchQueryRef = useRef('')
  /**
   * API-backed lists replace `options` while typing; the selected row may disappear from `options`
   * until the menu closes. Without a cache, the trigger would show raw `value` (e.g. company id).
   */
  const selectedLabelByValueRef = useRef<Map<string, string>>(new Map())
  const [menuPosition, setMenuPosition] = useState<{
    top: number | 'auto'
    bottom: number | 'auto'
    left: number
    width: number
  }>({ top: 0, bottom: 'auto', left: 0, width: 0 })

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return
    const r = triggerRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const calculatedWidth = Math.max(r.width, 240) // ensure enough width to avoid truncating
    let safeLeft = r.left

    // Shift left if overflowing viewport on the right
    if (safeLeft + calculatedWidth > viewportWidth - 16) {
      safeLeft = Math.max(16, viewportWidth - calculatedWidth - 16)
    }

    const spaceBelow = viewportHeight - r.bottom
    const spaceAbove = r.top
    const visibleOptions = Math.min(Math.max(options.length, 1), 6)
    const estimatedHeight = Math.min(
      280,
      (searchable ? 56 : 8) + visibleOptions * 40 + 8,
    )

    let top: number | 'auto' = r.bottom + 4
    let bottom: number | 'auto' = 'auto'

    const shouldOpenAbove =
      menuPlacement === 'top' ||
      (menuPlacement === 'auto' &&
        spaceBelow < estimatedHeight &&
        spaceAbove > spaceBelow)

    if (shouldOpenAbove) {
      top = 'auto'
      bottom = viewportHeight - r.top + 4
    }

    setMenuPosition({ top, bottom, left: safeLeft, width: calculatedWidth })
  }, [menuPlacement, options.length, searchable])

  /** Reset local search; optionally notify parent with empty query (see `invokeSearchOnClose`). */
  const closeDropdown = useCallback(() => {
    const hadPendingDebounce = searchDebounceRef.current != null
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
      searchDebounceRef.current = null
    }
    setIsOpen(false)
    const latestFromInput = inputRef.current?.value ?? latestSearchQueryRef.current
    setSearchText('')
    /**
     * If the user closed the menu before the debounce fired, `onSearch` would never run with the
     * final string (e.g. only "li" ran, then they typed "b" and closed — flush "lib" now).
     * When `invokeSearchOnClose` is true, parent reset uses `onSearch('')` only (no flush).
     */
    if (hadPendingDebounce && onSearch && searchDebounceMs > 0 && !invokeSearchOnClose) {
      onSearch(latestFromInput)
    }
    if (invokeSearchOnClose) {
      onSearch?.('')
    }
    latestSearchQueryRef.current = ''
  }, [onSearch, invokeSearchOnClose, searchDebounceMs])

  useLayoutEffect(() => {
    if (!isOpen) return
    updateMenuPosition()
    const onScrollOrResize = () => updateMenuPosition()
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [isOpen, updateMenuPosition])

  // Keep display labels in sync when `options` includes the current value(s).
  useEffect(() => {
    if (multiple && Array.isArray(value)) {
      for (const v of value) {
        const opt = options.find((o) => String(o.value) === String(v))
        if (opt?.label != null && String(opt.label) !== '') {
          selectedLabelByValueRef.current.set(String(v), String(opt.label))
        }
      }
      return
    }
    if (!multiple && value != null && String(value) !== '') {
      const v = String(value)
      const opt = options.find((o) => String(o.value) === v)
      if (opt?.label != null && String(opt.label) !== '') {
        selectedLabelByValueRef.current.set(v, String(opt.label))
      }
    }
  }, [options, value, multiple])

  // Close dropdown when clicking outside (trigger + portaled menu)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node
      if (containerRef.current?.contains(t)) return
      if (menuRef.current?.contains(t)) return
      closeDropdown()
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      if (searchable) {
        setTimeout(() => inputRef.current?.focus(), 0)
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, closeDropdown])

  // If the trigger becomes disabled while the menu is open (e.g. submit in flight), close so the portaled menu is not still usable.
  useEffect(() => {
    if (disabled && isOpen) closeDropdown()
  }, [disabled, isOpen, closeDropdown])

  // Client filter in the open menu; keep the current single value visible even if label does not match searchText.
  const filteredOptions = (() => {
    const q = searchText.toLowerCase()
    const base = options.filter((option) =>
      String(option.label ?? '')
        .toLowerCase()
        .includes(q),
    )
    if (multiple || value == null || String(value) === '') return base
    const v = String(value)
    const selected = options.find((o) => String(o.value) === v)
    if (!selected) return base
    if (base.some((o) => String(o.value) === v)) return base
    return [selected, ...base]
  })()

  const handleSelect = (option: SearchableSelectOption) => {
    const optionValue = option.value
    const label = String(option.label ?? '')
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : []
      if (currentValues.includes(optionValue)) {
        onChange(currentValues.filter((v) => v !== optionValue))
      } else {
        if (label) selectedLabelByValueRef.current.set(String(optionValue), label)
        onChange([...currentValues, optionValue])
      }
    } else {
      if (String(optionValue) === String(value ?? '')) {
        closeDropdown()
        return
      }
      if (label) selectedLabelByValueRef.current.set(String(optionValue), label)
      onChange(optionValue)
      closeDropdown()
    }
  }

  const handleRemove = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (multiple && Array.isArray(value)) {
      onChange(value.filter((v) => v !== optionValue))
    }
  }

  const labelForValue = (v: string) => {
    const opt = options.find((o) => String(o.value) === String(v))
    if (opt?.label != null && String(opt.label) !== '') return String(opt.label)
    const cached = selectedLabelByValueRef.current.get(String(v))
    if (cached) return cached
    if (loading) return 'Loading...'
    return String(v)
  }

  const getDisplayValue = () => {
    if (multiple && Array.isArray(value)) {
      if (value.length === 0) return placeholder
      if (value.length === 1) {
        return labelForValue(String(value[0]))
      }
      return `${value.length} selected`
    }
    if (!multiple && value != null && String(value) !== '') {
      return labelForValue(String(value))
    }
    return placeholder
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value
    latestSearchQueryRef.current = text
    setSearchText(text)
    if (!onSearch) return
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
      searchDebounceRef.current = null
    }
    const delay = searchDebounceMs > 0 ? searchDebounceMs : 0
    if (delay === 0) {
      onSearch(text)
      return
    }
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null
      onSearch(latestSearchQueryRef.current)
    }, delay)
  }

  const handleSearchPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Paste-specific handler takes precedence so a consumer can bypass a throttled `onSearch`.
    const pasteFn = onPasteSearch ?? onSearch
    const pasted = e.clipboardData?.getData('text') ?? ''
    const el = e.currentTarget
    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? el.value.length
    const next = el.value.slice(0, start) + pasted + el.value.slice(end)
    e.preventDefault()
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
      searchDebounceRef.current = null
    }
    latestSearchQueryRef.current = next
    setSearchText(next)
    if (pasteFn) {
      pasteFn(next)
    }
  }

  // Clear debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
      }
    }
  }, [])

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        data-searchable-select-trigger
        data-testid={triggerDataTestId}
        onClick={() => {
          if (!disabled) {
            const willOpen = !isOpen
            if (willOpen) {
              setIsOpen(true)
              latestSearchQueryRef.current = ''
              onOpen?.()
            } else {
              closeDropdown()
            }
          }
        }}
        onBlur={() => onBlur?.()}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        className={cn(
          'w-full min-h-[38px] h-[38px] rounded-md border px-3 text-sm text-left flex items-center justify-between gap-2',
          'bg-white text-slate-900 shadow-sm',
          'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500',
          error
            ? '!border-red-500 focus:!ring-red-500 focus:!border-red-500'
            : 'border-slate-300 hover:border-slate-400',
          disabled && 'opacity-50 cursor-not-allowed bg-slate-50',
          className
        )}
      >
        <span
          className={cn(
            multiple && Array.isArray(value) && value.length > 0
              ? 'truncate text-base font-semibold text-slate-800'
              : 'truncate',
            (!value ||
              (Array.isArray(value) && value.length === 0) ||
              (typeof value === 'string' && value === '')) &&
              'text-sm font-normal text-gray-400',
          )}
        >
          {/* eslint-disable-next-line react-hooks/refs -- render-through label cache: labelForValue falls back to a ref Map of previously-selected labels for values not in the current (async) options list; kept fresh by effects/handlers, intentional */}
          {getDisplayValue()}
        </span>
        <ChevronDown
          className={cn(
            'h-5 w-5 text-gray-400 flex-shrink-0 ml-2 transition-transform',
            isOpen && 'transform rotate-180'
          )}
        />
      </button>

      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={menuRef}
            className="flex max-h-60 flex-col overflow-hidden bg-white border border-gray-300 rounded-lg shadow-lg"
            style={{
              position: 'fixed',
              top: menuPosition.top,
              bottom: menuPosition.bottom,
              left: menuPosition.left,
              width: menuPosition.width,
              zIndex: MENU_PORTAL_Z,
            }}
          >
            {searchable && (
              <div className="shrink-0 p-2 border-b border-gray-200">
                <div className="relative searchable-select-search">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchText}
                    onChange={handleSearchChange}
                    onPaste={handleSearchPaste}
                    placeholder={searchPlaceholder}
                    className="w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ paddingLeft: '3rem', paddingRight: '0.75rem', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
                  />
                </div>
              </div>
            )}

            <div className={cn('min-h-0 flex-1 overflow-y-auto', !searchable && 'py-1')}>
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading...</div>
              ) : filteredOptions.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {options.length > 0 && searchText.trim() !== ''
                    ? `No matches for "${searchText.trim()}"`
                    : 'No options available'}
                </div>
              ) : (
                filteredOptions.map((option, idx) => {
                  const isSelected = multiple
                    ? Array.isArray(value) && value.includes(option.value)
                    : String(value ?? '') === String(option.value)

                  return (
                    <div
                      key={`${String(option.value)}-${idx}`}
                      onClick={() => handleSelect(option)}
                      className={cn(
                        'px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center justify-between',
                        isSelected && 'bg-blue-50 text-blue-700'
                      )}
                    >
                      <span className="text-sm">{option.label}</span>
                      {isSelected && (
                        <span className="text-blue-600 text-sm">✓</span>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </div>,
          document.body
        )}

      {/* Selected values chips (for multiple) — label wraps, white dismiss control */}
      {multiple && Array.isArray(value) && value.length > 0 && (
        <div
          data-searchable-select-chips
          className={cn(
            'mt-3 gap-2',
            selectedChipsLayout === 'column' && 'flex flex-col',
            selectedChipsLayout === 'grid-2' && 'grid grid-cols-2',
            selectedChipsLayout === 'flex-wrap' && 'flex flex-wrap',
          )}
        >
          {/* eslint-disable-next-line react-hooks/refs -- chip labels fall back to selectedLabelByValueRef for async option lists */}
          {value.map((val) => {
            const option = options.find((opt) => opt.value === val)
            const chipLabel = option?.label || labelForValue(String(val))
            return (
              <div
                key={val}
                className={cn(
                  'flex items-stretch gap-2 rounded-xl bg-sky-50 px-3 py-2.5 text-sky-900 ring-1 ring-sky-100',
                  selectedChipsLayout === 'flex-wrap' ? 'w-fit max-w-full' : 'min-w-0',
                )}
              >
                <span
                  className={cn(
                    'text-left text-sm font-medium leading-snug text-blue-700',
                    selectedChipsLayout === 'flex-wrap'
                      ? 'min-w-0 break-words'
                      : 'min-w-0 whitespace-normal',
                    selectedChipsLayout === 'column' && 'flex-1',
                  )}
                >
                  {chipLabel}
                </span>
                <button
                  type="button"
                  data-searchable-select-chip-dismiss
                  onClick={(e) => handleRemove(val, e)}
                  className="flex shrink-0 items-center justify-center self-stretch rounded-lg border border-slate-200/90 bg-white px-2.5 text-blue-600 shadow-sm transition-colors hover:bg-slate-50"
                  aria-label={`Remove ${chipLabel}`}
                >
                  <X className="h-4 w-4" strokeWidth={2.25} />
                </button>
              </div>
            )
          })}
        </div>
      )}

      {error && showErrorMessage && (
        <span role="alert" className="mt-1 block text-sm text-red-600">
          {error}
        </span>
      )}
    </div>
  )
}
